import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Field } from "./Field";

const EMPTY = { name: "", description: "", amenities: "", capacity: "", price: "", show_price: false, notes: "", whatsapp_message: "", published: false, order: 0 };

const Toggle = ({ label, hint, checked, onChange, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2">
    <div><Label className="text-sm">{label}</Label>{hint && <p className="text-xs text-zinc-500 mt-1">{hint}</p>}</div>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export const RoomForm = ({ room, open, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setF(room ? { ...EMPTY, ...room, amenities: (room.amenities || []).join("\n") } : EMPTY);
  }, [room, open]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.name.trim()) return toast.error("Informe o nome da acomodação.");
    setBusy(true);
    const body = {
      name: f.name.trim(), description: f.description, capacity: f.capacity, price: f.price, show_price: f.show_price,
      notes: f.notes, whatsapp_message: f.whatsapp_message, published: f.published, order: Number(f.order) || 0,
      amenities: f.amenities.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (room?.id) await api.put(`/admin/accommodations/${room.id}`, body);
      else await api.post("/admin/accommodations", body);
      toast.success("Acomodação salva");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="room-form-dialog" className="max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border-zinc-800 w-[95vw] max-w-xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{room?.id ? "Editar acomodação" : "Nova acomodação"}</DialogTitle></DialogHeader>
        <Field label="Nome *" value={f.name} onChange={set("name")} testid="room-input-name" />
        <Field label="Descrição" value={f.description} onChange={set("description")} textarea rows={5} testid="room-input-description" />
        <Field label="Comodidades confirmadas" hint="Uma por linha. Informe apenas o que a acomodação realmente oferece." value={f.amenities} onChange={set("amenities")} textarea rows={4} testid="room-input-amenities" />
        <Field label="Capacidade" hint="Ex.: até 2 hóspedes. Deixe vazio se não se aplica." value={f.capacity} onChange={set("capacity")} testid="room-input-capacity" />
        <Field label="Preço (opcional)" hint="Ex.: a partir de R$ 000 / diária" value={f.price} onChange={set("price")} testid="room-input-price" />
        <Toggle label="Exibir preço no site" hint="O preço só aparece quando esta opção estiver ativa." checked={f.show_price} onChange={set("show_price")} testid="room-input-show-price" />
        <Field label="Observações" value={f.notes} onChange={set("notes")} textarea testid="room-input-notes" />
        <Field label="Mensagem de WhatsApp (opcional)" hint="Se vazio, usa a mensagem padrão com o nome da acomodação." value={f.whatsapp_message} onChange={set("whatsapp_message")} textarea testid="room-input-whatsapp" />
        <Field label="Ordem de exibição" type="number" inputMode="numeric" value={f.order} onChange={set("order")} testid="room-input-order" />
        <Toggle label="Publicada" hint="Desative para ocultar do site." checked={f.published} onChange={set("published")} testid="room-input-published" />
        <button onClick={save} disabled={busy} data-testid="room-save-btn" className="btn-gold w-full h-12">
          {busy ? <Loader2 className="animate-spin" size={18} /> : "Salvar alterações"}
        </button>
      </DialogContent>
    </Dialog>
  );
};
